import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';

export interface TimerState {
  progress: number;
  showTimer: boolean;
  timerBonus: number;
}

export const useTimerStore = defineStore('timerStore', () => {
  // State
  const progress = ref<number>(0);
  const showTimer = ref(false);
  const timerBonus = ref(0);

  // Private state
  const timerId = ref<number | null>(null);
  const updateIntervalId = ref<number | null>(null);
  const timerResolver = shallowRef<((value: unknown) => void) | null>(null);
  const startTime = ref<number>(0);
  const endTime = ref<number>(0);
  const durationMs = ref<number>(0);

  const animationFrameId = ref<number | null>(null);
  const timerEndTime = ref<number>(0);

  function stopAndHideTimer(resolutionValue = true) {
    // Clear any pending timers
    if (timerId.value !== null) {
      clearTimeout(timerId.value);
      timerId.value = null;
    }

    // Clear update interval
    if (updateIntervalId.value !== null) {
      clearInterval(updateIntervalId.value);
      updateIntervalId.value = null;
    }

    // Clear animation frame
    if (animationFrameId.value !== null) {
      cancelAnimationFrame(animationFrameId.value);
      animationFrameId.value = null;
    }

    // Resolve any pending promise
    if (timerResolver.value) {
      timerResolver.value(resolutionValue);
      timerResolver.value = null;
    }

    showTimer.value = false;
    progress.value = 0;
  }

  function updateProgress() {
    const now = performance.now();
    const elapsed = now - startTime.value;
    const totalDuration = endTime.value - startTime.value;

    // Calculate progress (0-1000)
    progress.value = Math.min(1000, (elapsed / totalDuration) * 1000);

    // Add logging every 500ms (to avoid console spam)
    if (Math.floor(elapsed / 500) > Math.floor((elapsed - 16) / 500)) {
      // console.log(
      //   `Progress: ${progress.value}/1000, Elapsed: ${elapsed.toFixed(0)}ms, Total: ${totalDuration.toFixed(0)}ms`
      // );
    }

    // Check if timer completed
    if (now >= endTime.value) {
      progress.value = 1000;
      stopAndHideTimer(true);
    }
  }

  function startTimer(durationInSeconds = 2, isAsync = false): void {
    // Clean up any existing timer
    stopAndHideTimer();

    // Setup new timer
    showTimer.value = true;
    progress.value = 0;

    // Calculate end time once instead of on each frame
    const startTime = performance.now();
    timerEndTime.value = startTime + durationInSeconds * 1000;

    const updateTimer = () => {
      const now = performance.now();

      // If timer should be complete
      if (now >= timerEndTime.value) {
        progress.value = 1000;
        showTimer.value = false;
        animationFrameId.value = null;
        return;
      }

      // Calculate progress percentage (0-1000)
      progress.value = Math.min(
        1000,
        ((now - startTime) / (durationInSeconds * 1000)) * 1000
      );

      // Schedule next update
      animationFrameId.value = requestAnimationFrame(updateTimer);
    };

    // Start the timer loop
    if (isAsync) {
      setTimeout(() => {
        animationFrameId.value = requestAnimationFrame(updateTimer);
      }, 0);
    } else {
      animationFrameId.value = requestAnimationFrame(updateTimer);
    }
  }

  /**
   * Start a timer with a specific duration
   * @param durationInSeconds Duration in seconds
   * @returns Promise that resolves when timer completes
   */
  async function startTimerAsync(durationInSeconds: number): Promise<boolean> {
    // Stop any existing timer
    stopAndHideTimer(false);

    // Setup timer state
    showTimer.value = true;
    progress.value = 0;
    startTime.value = performance.now();
    durationMs.value = durationInSeconds * 1000;
    endTime.value = startTime.value + durationMs.value;

    return new Promise<boolean>((resolve) => {
      timerResolver.value = resolve;

      // Use requestAnimationFrame for smooth updates
      const updateFrame = () => {
        const now = performance.now();
        const elapsed = now - startTime.value;

        // Check if timer is complete
        if (now >= endTime.value) {
          progress.value = 1000;
          showTimer.value = false;

          if (timerResolver.value) {
            timerResolver.value(true);
            timerResolver.value = null;
          }

          // Clear the animation frame
          if (animationFrameId.value !== null) {
            cancelAnimationFrame(animationFrameId.value);
            animationFrameId.value = null;
          }
          return;
        }

        // Update progress (0-1000)
        progress.value = Math.min(1000, (elapsed / durationMs.value) * 1000);

        // Schedule next frame
        animationFrameId.value = requestAnimationFrame(updateFrame);
      };

      // Start the animation loop
      animationFrameId.value = requestAnimationFrame(updateFrame);
    });
  }

  return {
    // State
    progress,
    showTimer,
    timerBonus,

    // Actions
    stopAndHideTimer,
    startTimerAsync,
    startTimer,
  };
});
